import { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Play, ArrowRight, MapPin, Calendar, Edit, Bird, Clock, Mic, HandHeart, Link2 } from "lucide-react"
import { SpeakerApplicationDialog } from "@/components/SpeakerApplicationDialog"
import { VolunteerApplicationForm } from "@/components/VolunteerApplicationForm"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useActiveEarlyBird } from "@/hooks/useActiveEarlyBird"
import { useImageUpload } from "@/hooks/useImageUpload"
import { useEventDetail } from "@/hooks/useEventDetail"
import { useHeroImage } from "@/hooks/useHeroImage"
import type { EventType } from "@/utils/eventUtils"
import { format } from "date-fns"
import { parseLocalDate } from "@/lib/utils"
import { toast } from "sonner"
import { useEditMode } from "@/components/EditModeProvider"
import { HeroContentEditor } from "@/components/admin/HeroContentEditor"
import { EventImagesProvider } from "@/contexts/EventImagesContext"
import { preloadHeroImage } from "@/utils/imagePreloader"
import { useComponentLoadTime } from "@/hooks/usePerformanceMetrics"
import { YouTubeBackground, isYouTubeUrl } from "@/components/YouTubeBackground"
import { scrollToSection, findFirstSection } from "@/lib/scrollToSection"

interface FloatingKeyword {
  text: string
  color: string
  position: { x: number; y: number }
  delay: number
}

interface DynamicHeroSectionProps {
  eventId?: string
  eventType?: EventType
  title?: string
  subtitle?: string
  description?: string
  primaryCTA?: string
  secondaryCTA?: string
  primaryCTALink?: string
  secondaryCTALink?: string
  videoUrl?: string
  stats?: Array<{
    value: string
    label: string
  }>
  category?: string
  backgroundImageOverride?: string
  backgroundVideoUrl?: string
  backgroundSettingsOverride?: {
    position?: string
    size?: string
    repeat?: string
    opacity?: number
  }
  disableInlineEdit?: boolean
  cyclingWords?: string[]
  staticPrefix?: string
  heroTagline?: string
  phrases?: string[]
}

const DynamicHeroSection = ({
  eventId,
  eventType = 'nomad-fest',
  title: propTitle,
  subtitle: propSubtitle,
  description: propDescription,
  primaryCTA = "Get Your Tickets Now",
  secondaryCTA = "Learn More",
  primaryCTALink,
  secondaryCTALink,
  videoUrl,
  stats: propStats,
  category = 'festivals',
  backgroundImageOverride,
  backgroundVideoUrl,
  backgroundSettingsOverride,
  disableInlineEdit = false,
  cyclingWords = [],
  staticPrefix,
  heroTagline,
  phrases,
}: DynamicHeroSectionProps) => {
  useComponentLoadTime('DynamicHeroSection');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isEditMode } = useEditMode();
  const [floatingKeywords, setFloatingKeywords] = useState<FloatingKeyword[]>([])
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showSpeakerDialog, setShowSpeakerDialog] = useState(false)
  const [showVolunteerDialog, setShowVolunteerDialog] = useState(false)

  // Auto-open dialog from URL: ?apply=speaker or ?apply=volunteer
  useEffect(() => {
    const apply = searchParams.get('apply');
    if (apply === 'speaker') setShowSpeakerDialog(true);
    if (apply === 'volunteer') setShowVolunteerDialog(true);
  }, []);

  const copyApplyLink = (type: 'speaker' | 'volunteer') => {
    const url = `${window.location.origin}${window.location.pathname}?apply=${type}`;
    navigator.clipboard.writeText(url).then(() => {
      import('sonner').then(({ toast }) => toast.success(`${type === 'speaker' ? 'Speaker' : 'Volunteer'} application link copied!`));
    });
  };

  // Lightweight word rotation. Avoid per-character React updates, which made
  // the preview feel frozen and flooded session replay/console output.
  const [typedWord, setTypedWord] = useState(() => cyclingWords[0] ?? '');
  const wordIndexRef = useRef(0);
  const cyclingWordsKey = cyclingWords.join('\u0001');

  useEffect(() => {
    if (!cyclingWords.length) {
      setTypedWord('');
      return;
    }

    wordIndexRef.current = 0;
    setTypedWord(cyclingWords[0]);
    if (cyclingWords.length === 1) return;

    const interval = window.setInterval(() => {
      wordIndexRef.current = (wordIndexRef.current + 1) % cyclingWords.length;
      setTypedWord(cyclingWords[wordIndexRef.current]);
    }, 4000);

    return () => window.clearInterval(interval);
  }, [cyclingWordsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [typedPhrase, setTypedPhrase] = useState(() => phrases?.[0] ?? '');
  const phraseIndexRef = useRef(0);
  const phrasesKey = phrases?.join('\u0001') ?? '';

  useEffect(() => {
    if (!phrases?.length) {
      setTypedPhrase('');
      return;
    }

    phraseIndexRef.current = 0;
    setTypedPhrase(phrases[0]);
    if (phrases.length === 1) return;

    const interval = window.setInterval(() => {
      phraseIndexRef.current = (phraseIndexRef.current + 1) % phrases.length;
      setTypedPhrase(phrases[phraseIndexRef.current]);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [phrasesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { event, loading } = useEventDetail(category, eventId || '');
  const { heroImage, loading: heroImageLoading } = useHeroImage(eventId || '', eventType);
  const { topCampaign } = useActiveEarlyBird(eventId);

  // Generate floating keywords based on event data from database
  useEffect(() => {
    if (!event || !event.topics || event.topics.length === 0) return;

    const getKeywordColors = () => [
      'from-blue-400 to-cyan-300',
      'from-green-400 to-emerald-300', 
      'from-orange-400 to-yellow-300',
      'from-purple-400 to-pink-300',
      'from-teal-400 to-blue-300',
      'from-red-400 to-orange-300',
      'from-indigo-400 to-purple-300',
      'from-pink-400 to-rose-300'
    ];

    const colors = getKeywordColors();
    const positionedKeywords = event.topics.map((topic, index) => ({
      text: topic,
      color: colors[index % colors.length],
      position: {
        x: 10 + (index % 3) * 30 + Math.random() * 15,
        y: 15 + Math.floor(index / 3) * 25 + Math.random() * 10
      },
      delay: index * 0.2
    }));

    setFloatingKeywords(positionedKeywords);
  }, [event]);


  // Use event data or fallback to props
  const title = event?.name || propTitle || "Welcome to Spark Connect";
  const subtitle = event?.theme?.replace('-', ' ').toUpperCase() || propSubtitle || "Connection Platform";  
  const description = event?.description || propDescription || "Building meaningful connections in the digital age.";
  const location = event?.location;
  const startDate = event?.start_date;
  const endDate = event?.end_date;

  // Generate stats based on event data or use defaults
  const stats = propStats || [
    { value: event?.capacity ? event.capacity.toString() : "50+", label: "Participants" },
    { value: startDate && endDate ? `${Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))}` : "7", label: "Days" },
    { value: "Epic", label: "Experience" },
    { value: "100%", label: "Satisfaction" }
  ];

  // Use override background if provided, otherwise use fetched hero image or event's main image
  const effectiveBackgroundUrl = backgroundImageOverride || heroImage || event?.image_url;
  
  // Preload hero image for faster loading - MUST be before conditional return
  useEffect(() => {
    if (effectiveBackgroundUrl) {
      preloadHeroImage(effectiveBackgroundUrl);
    }
  }, [effectiveBackgroundUrl]);

  if ((loading || heroImageLoading) && eventId) {
    return (
      <section className="relative h-screen flex items-center justify-center overflow-hidden w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-24">
        {/* Navigation Spacer - prevents content from being hidden under fixed nav */}
        <div className="h-48 flex-shrink-0 absolute top-0 left-0 right-0 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent opacity-90" />
        
        {/* Loading skeleton with fixed dimensions matching loaded content */}
        <div className="relative z-10 text-white text-center max-w-5xl mx-auto px-4 w-full h-[600px] flex flex-col justify-center">
          {/* Subtitle skeleton */}
          <div className="h-6 w-48 bg-white/20 rounded mx-auto mb-3" />
          
          {/* Title skeleton */}
          <div className="h-16 w-3/4 max-w-2xl bg-white/20 rounded mx-auto mb-6" />
          
          {/* Location and date skeleton - fixed height */}
          <div className="flex items-center justify-center gap-6 mb-8 h-[3rem]">
            <div className="h-6 w-40 bg-white/20 rounded" />
            <div className="h-6 w-64 bg-white/20 rounded" />
          </div>
          
          {/* Description skeleton */}
          <div className="space-y-3 mb-12 max-w-3xl mx-auto">
            <div className="h-5 w-full bg-white/20 rounded" />
            <div className="h-5 w-5/6 bg-white/20 rounded mx-auto" />
          </div>
          
          {/* CTA buttons skeleton */}
          <div className="flex items-center justify-center gap-6 mb-16">
            <div className="h-12 w-48 bg-white/20 rounded" />
            <div className="h-12 w-40 bg-white/20 rounded" />
          </div>
          
          {/* Stats skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="text-center">
                <div className="h-10 w-20 bg-white/20 rounded mx-auto mb-2" />
                <div className="h-4 w-24 bg-white/20 rounded mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const effectiveSettings = backgroundSettingsOverride || {
    position: 'center',
    size: 'cover',
    repeat: 'no-repeat',
    opacity: 100
  };

  return (
    <>
      {/* Edit Dialog */}
      {eventId && eventType && (
        <EventImagesProvider eventId={eventId} eventType={eventType}>
          <HeroContentEditor
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            eventId={eventId}
            eventType={eventType}
            category={category}
            currentData={{
              title,
              description,
              location,
              startDate,
              endDate,
              primaryCTA,
              secondaryCTA,
              primaryCTALink: event?.primary_cta_link,
              secondaryCTALink: event?.secondary_cta_link,
              stats,
            }}
            onSuccess={() => {
              // Reload event data
              window.location.reload();
            }}
          />
        </EventImagesProvider>
      )}

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-24">
        {/* Navigation Spacer - prevents content from being hidden under fixed nav */}
        <div className="h-48 flex-shrink-0 absolute top-0 left-0 right-0 pointer-events-none" />
        {/* Edit Button - Positioned inline with edit controls */}
        {isEditMode && eventId && !disableInlineEdit && (
          <Button
            onClick={() => setShowEditDialog(true)}
            className="absolute top-4 left-4 z-20 gap-2 shadow-lg"
            variant="secondary"
            size="sm"
          >
            <Edit className="h-4 w-4" />
            <span className="hidden sm:inline">Edit Hero</span>
          </Button>
        )}

        {/* Background - Video or Image */}
        {backgroundVideoUrl ? (
          <>
            {isYouTubeUrl(backgroundVideoUrl) ? (
              <YouTubeBackground videoUrl={backgroundVideoUrl} />
            ) : (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              >
                <source src={backgroundVideoUrl} type="video/mp4" />
              </video>
            )}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/60 via-purple-600/60 to-pink-600/60 pointer-events-none" />
          </>
        ) : effectiveBackgroundUrl ? (
          <>
            <div 
              className="absolute inset-0 w-full h-full pointer-events-none hero-bg"
              style={{
                backgroundImage: `url(${effectiveBackgroundUrl})`,
                backgroundSize: effectiveSettings.size,
                backgroundRepeat: effectiveSettings.repeat,
                opacity: (effectiveSettings.opacity || 100) / 100
              }}
            />
            {/* Two-layer scrim for a premium, legible hero over busy photos:
                (1) an even base darkening so the centred title reads cleanly,
                (2) a cinematic bottom-up fade so the CTAs sit on the darkest area. */}
            <div className="absolute inset-0 bg-black/45 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30 pointer-events-none" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 pointer-events-none" />
        )}

        {/* Content with fixed min-height to prevent shift */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full flex flex-col justify-center pt-48">
          <div className="max-w-5xl mx-auto w-full min-h-[400px] flex flex-col justify-center">
            {/* Early Bird Banner */}
            {topCampaign && (
              <div className="mb-4 sm:mb-6 flex justify-center px-4">
                <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-gradient-to-r from-amber-500/90 to-orange-500/90 backdrop-blur-sm border border-white/20 shadow-lg animate-pulse">
                  <Bird className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  <span className="text-white font-semibold text-sm sm:text-base">
                    {topCampaign.badge_label || topCampaign.name}
                    {' — '}
                    {topCampaign.discount_type === 'percentage'
                      ? `${topCampaign.discount_value}% off`
                      : `${topCampaign.discount_value} CHF off`}
                  </span>
                  {topCampaign.end_date && (
                    <span className="text-white/80 text-xs sm:text-sm flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      ends {(parseLocalDate(topCampaign.end_date) ?? new Date(topCampaign.end_date)).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Main Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-3 leading-tight px-2">
              {cyclingWords.length > 0 ? (
                staticPrefix ? (
                  <>
                    {staticPrefix}{' '}
                    <span className="text-primary">
                      {typedWord}
                      <span className="inline-block w-[3px] h-[0.9em] bg-primary ml-0.5 align-middle animate-pulse" />
                    </span>
                  </>
                ) : (
                  <>
                    {title}{' '}
                    <span className="text-primary">
                      {typedWord}
                      <span className="inline-block w-[3px] h-[0.9em] bg-primary ml-0.5 align-middle animate-pulse" />
                    </span>
                  </>
                )
              ) : (
                <span>{staticPrefix || title}</span>
              )}
            </h1>

            {/* Subtitle + tagline */}
            <p className="text-xs sm:text-sm uppercase tracking-widest text-white/60 font-semibold mb-2 px-2">
              {subtitle}
            </p>
            {heroTagline && (
              <p className="text-base sm:text-lg text-white/50 mb-4 px-2">
                {heroTagline}
              </p>
            )}

            {/* Location and Date */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-6 sm:mb-8 px-4 h-[3rem]">
              {location && (
                <div className="flex items-center gap-2 text-cyan-300 text-base sm:text-lg font-medium">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                  <span className="text-center">{location}</span>
                </div>
              )}
              
              {startDate && endDate && (
                <div className="flex items-center gap-2 text-white text-sm sm:text-base md:text-lg font-medium text-center">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                  <span className="break-words">
                    {format(parseLocalDate(startDate) ?? new Date(startDate), 'EEEE, MMMM d, yyyy')} - {format(parseLocalDate(endDate) ?? new Date(endDate), 'EEEE, MMMM d, yyyy')}
                  </span>
                </div>
              )}
            </div>
            
            {/* Description (non-phrase mode) */}
            {(!phrases || phrases.length === 0) && (
              // Full description, shown compact: smaller + narrower so it stays tidy
              <p className="text-sm sm:text-base text-white/85 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-4">
                {description}
              </p>
            )}
          
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-12 sm:mb-16 px-4">
              <Button 
                size="lg" 
                className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white border-0 shadow-lg transform hover:scale-105 transition-all duration-300"
                onClick={() => {
                  const link = primaryCTALink || event?.primary_cta_link;
                  if (link) {
                    if (link.startsWith('/')) {
                      navigate(link);
                    } else if (link.startsWith('#')) {
                      // Handle anchor scroll
                      scrollToSection(link);
                    } else {
                      window.open(link, '_blank');
                    }
                  } else {
                    // Default: scroll to the shop (tickets first). The old
                    // "accommodation" block renders nothing, so it is only a last resort.
                    const shopSection = findFirstSection([
                      'shop',
                      'section-shop',
                      'section-event_shop',
                      'section-tickets_shop',
                      'section-booking',
                      'section-accommodation',
                    ]);
                    if (shopSection) {
                      scrollToSection(shopSection);
                    } else if (eventId) {
                      navigate(`/events/${category}/${eventId}`);
                    } else {
                      navigate('/events');
                    }
                  }
                }}
              >
                {primaryCTA}
              </Button>
              
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm"
                onClick={() => {
                  const link = secondaryCTALink || event?.secondary_cta_link;
                  if (link) {
                    if (link.startsWith('/')) {
                      navigate(link);
                    } else if (link.startsWith('#')) {
                      scrollToSection(link);
                    } else {
                      window.open(link, '_blank');
                    }
                  } else {
                    // Default: scroll to schedule/program section
                    const scheduleSection = findFirstSection([
                      'section-schedule',
                      'section-program',
                      'section-whats_included',
                      'section-about',
                    ]);
                    if (scheduleSection) {
                      scrollToSection(scheduleSection);
                    } else if (eventId) {
                      navigate(`/events/${category}/${eventId}`);
                    } else {
                      navigate('/events');
                    }
                  }
                }}
              >
                {videoUrl && <Play className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />}
                {secondaryCTA || "View Details"}
              </Button>
            </div>

            {/* Apply Buttons */}
            {eventId && (
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
                {/* Speaker apply + copy link */}
                <div className="flex items-center rounded-xl overflow-hidden shadow-xl ring-1 ring-white/10">
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm sm:text-base rounded-none border-0 px-5 sm:px-6 py-2.5 sm:py-3"
                    onClick={() => setShowSpeakerDialog(true)}
                  >
                    <Mic className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Apply as Speaker
                  </Button>
                  <Button
                    className="bg-primary/80 hover:bg-primary text-primary-foreground rounded-none border-0 px-3 py-2.5 sm:py-3"
                    title="Copy shareable link"
                    onClick={() => copyApplyLink('speaker')}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Volunteer apply + copy link */}
                <div className="flex items-center rounded-xl overflow-hidden shadow-xl ring-1 ring-black/5">
                  <Button
                    className="bg-white hover:bg-white/90 text-primary font-bold text-sm sm:text-base rounded-none border-0 px-5 sm:px-6 py-2.5 sm:py-3"
                    onClick={() => setShowVolunteerDialog(true)}
                  >
                    <HandHeart className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Apply as Volunteer
                  </Button>
                  <Button
                    className="bg-white/80 hover:bg-white text-primary rounded-none border-0 px-3 py-2.5 sm:py-3"
                    title="Copy shareable link"
                    onClick={() => copyApplyLink('volunteer')}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 md:gap-8 max-w-5xl mx-auto mb-12 sm:mb-16 px-4">
              {stats.map((stat, index) => (
                <div key={index} className="text-center bg-black/20 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
                  <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2 break-words ${
                    index === 0 ? 'text-cyan-300' : 
                    index === 1 ? 'text-orange-300' : 
                    index === 2 ? 'text-cyan-300' : 
                    'text-green-300'
                  }`}>
                    {stat.value}
                  </div>
                  <div className="text-white/70 text-xs sm:text-sm md:text-base font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Cycling phrases — below stats, lower edge of hero */}
            {phrases && phrases.length > 0 && (
              <div className="px-4 pb-6 min-h-[4.5rem] flex items-center justify-center">
                <p className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-center leading-tight tracking-tight">
                  <span
                    className="inline-block mr-3 align-middle"
                    style={{ filter: 'drop-shadow(0 0 12px rgba(139,92,246,0.9)) drop-shadow(0 0 24px rgba(99,202,239,0.6))', lineHeight: 1 }}
                  >
                    ✦
                  </span>
                  <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(90deg, #67e8f9 0%, #a78bfa 40%, #f472b6 80%, #fbbf24 100%)' }}
                  >
                    {typedPhrase}
                  </span>
                  <span
                    className="inline-block w-[4px] rounded-full ml-2 align-middle animate-pulse"
                    style={{ height: '0.8em', background: 'linear-gradient(180deg, #67e8f9, #a78bfa)', verticalAlign: 'middle' }}
                  />
                </p>
              </div>
            )}

            {/* Floating Keywords - Now at bottom */}
            <div className="relative mt-8 sm:mt-12 hidden md:block px-4 pointer-events-none">
              {floatingKeywords.map((keyword, index) => (
                <div
                  key={index}
                  className="inline-block m-2 animate-float"
                  style={{
                    animationDelay: `${keyword.delay}s`,
                    animationDuration: '6s'
                  }}
                >
                  <div className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-white font-medium text-xs sm:text-sm bg-gradient-to-r ${keyword.color} backdrop-blur-sm border border-white/20 shadow-lg hover:scale-105 transition-transform duration-200`}>
                    {keyword.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </section>
    {eventId && (
      <SpeakerApplicationDialog
        eventId={eventId}
        open={showSpeakerDialog}
        onOpenChange={setShowSpeakerDialog}
      />
    )}
    {eventId && (
      <Dialog open={showVolunteerDialog} onOpenChange={setShowVolunteerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Apply as Volunteer</DialogTitle>
          </DialogHeader>
          <VolunteerApplicationForm eventId={eventId} onSuccess={() => setShowVolunteerDialog(false)} />
        </DialogContent>
      </Dialog>
    )}
    </>
  )
}

export { DynamicHeroSection }